import { useState, useEffect } from 'react';
import { View, Text, Pressable, Alert, ScrollView, Platform } from 'react-native';
import { Link, useRouter } from 'expo-router';
import { Input, PasswordInput, Card } from '../../components/ui';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../shared/utils/api';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { Ionicons } from '@expo/vector-icons';
import Animated, { 
  useSharedValue, 
  useAnimatedStyle, 
  withSpring,
  FadeInUp,
} from 'react-native-reanimated';

type RequestStatus = 'no_request' | 'pending' | 'approved' | 'denied' | 'account_exists' | 'checking';

export default function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>('no_request');
  const [checkingStatus, setCheckingStatus] = useState(false);
  const router = useRouter();
  const setAuth = useAuthStore((state) => state.setAuth);
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  const { isMobile } = responsive;

  // Button animations
  const buttonScale = useSharedValue(1);
  const buttonAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: buttonScale.value }],
  }));

  const handleButtonPressIn = () => {
    buttonScale.value = withSpring(0.96, { damping: 15, stiffness: 300 });
  };

  const handleButtonPressOut = () => {
    buttonScale.value = withSpring(1, { damping: 15, stiffness: 300 });
  };

  // Check request status when email changes (debounced)
  useEffect(() => {
    if (!email || email.length < 3) {
      setRequestStatus('no_request');
      return;
    }

    const checkStatus = async () => {
      try {
        setCheckingStatus(true);
        const response = await authApi.checkRequestStatus(email);
        setRequestStatus(response.status);
      } catch (error) {
        setRequestStatus('no_request');
      } finally {
        setCheckingStatus(false);
      }
    };

    const timeoutId = setTimeout(checkStatus, 500);
    return () => clearTimeout(timeoutId);
  }, [email]);

  const handleRequestAccount = async () => {
    if (!email) {
      Alert.alert('Error', 'Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.requestAccount(email);
      const statusResponse = await authApi.checkRequestStatus(email);
      setRequestStatus(statusResponse.status);
      Alert.alert('Request Submitted', response.message || 'Your account request has been submitted. You will be notified once it is reviewed.');
    } catch (error: any) {
      let errorMessage = 'Unable to submit account request. Please try again.';
      
      if (error.message) {
        if (error.message.includes('rate limit') || error.message.includes('too many')) {
          errorMessage = 'Too many requests. Please wait before requesting again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      Alert.alert('Request Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!email || !password) {
      Alert.alert('Error', 'Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      Alert.alert('Error', 'Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.signup({ email, password });
      // On web, token is in httpOnly cookie (not in response for security)
      // On mobile, token is in response
      const token = response.token || (Platform.OS === 'web' ? 'cookie' : null);
      setAuth(response.user, token || 'cookie');
      router.replace('/(tabs)/home');
    } catch (error: any) {
      let errorMessage = 'Unable to create account. Please try again.';
      
      if (error.message) {
        if (error.message.includes('Password does not meet requirements')) {
          errorMessage = 'Password must be at least 8 characters and contain uppercase, lowercase, and number.';
        } else if (error.message.includes('Account request pending')) {
          errorMessage = 'Your account request is pending approval. Please wait for administrator review.';
          const statusResponse = await authApi.checkRequestStatus(email);
          setRequestStatus(statusResponse.status);
        } else if (error.message.includes('Account creation failed') || error.message.includes('Unable to create account')) {
          errorMessage = 'Unable to create account. Please ensure your account request has been approved.';
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address.';
        } else {
          errorMessage = error.message;
        }
      }
      
      Alert.alert('Signup Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Responsive values
  const containerMaxWidth = getResponsiveValue(440, 500, 500, 520);
  const containerPadding = getResponsiveValue(
    utils.spacing.md,
    utils.spacing.lg,
    utils.spacing.xl,
    utils.spacing.xl
  );
  const headingSize = getResponsiveValue(28, 30, 32, 32);
  const subtitleSize = getResponsiveValue(16, 17, 18, 18);
  const bodySize = getResponsiveValue(15, 15, 16, 16);
  const buttonSize = getResponsiveValue(16, 17, 18, 18);

  const renderContent = () => {
    if (requestStatus === 'account_exists') {
      return (
        <>
          <Animated.View 
            entering={FadeInUp.delay(100).springify()}
            style={{ marginBottom: utils.spacing['2xl'], alignItems: 'center' }}
          >
            <View style={{
              width: getResponsiveValue(80, 90, 100, 100),
              height: getResponsiveValue(80, 90, 100, 100),
              borderRadius: utils.radius.xl,
              backgroundColor: tokens.color.primary,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: utils.spacing.lg,
              ...Platform.select({
                web: {
                  boxShadow: `0 8px 32px ${addColorOpacity(tokens.color.primary, 0.25)}`,
                } as any,
                default: {
                  shadowColor: tokens.color.primary,
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.25,
                  shadowRadius: 32,
                  elevation: 8,
                },
              }),
            }}>
              <Ionicons 
                name="leaf" 
                size={getResponsiveValue(40, 44, 48, 48)} 
                color={tokens.color.onPrimary} 
              />
            </View>
            <Text style={[
              {
                fontSize: headingSize,
                fontWeight: utils.typography.weights.bold,
                textAlign: 'center',
                marginBottom: utils.spacing.md,
                letterSpacing: -0.5,
                color: tokens.color.onBackground,
                fontFamily: utils.typography.heading.fontFamily,
                lineHeight: headingSize * 1.2,
                width: '100%',
              }
            ]}>
              Account Already Exists
            </Text>
            <Text style={[
              {
                fontSize: subtitleSize,
                color: tokens.color.onBackground,
                textAlign: 'center',
                marginBottom: utils.spacing['2xl'],
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: subtitleSize * 1.5,
                width: '100%',
                paddingHorizontal: utils.spacing.md,
              }
            ]}>
              An account with this email already exists. Please log in instead.
            </Text>
          </Animated.View>
          <Animated.View entering={FadeInUp.delay(200).springify()}>
            <Card style={{ 
              width: '100%',
              ...Platform.select({
                web: {
                  boxSizing: 'border-box',
                } as any,
              }),
            }}>
              <Animated.View style={[buttonAnimatedStyle, { width: '100%' }]}>
                <Pressable
                  onPressIn={handleButtonPressIn}
                  onPressOut={handleButtonPressOut}
                  onPress={() => router.push('/(auth)/login')}
                  style={({ pressed }) => [
                    {
                      backgroundColor: tokens.color.primary,
                      minHeight: getResponsiveValue(48, 50, 52, 52),
                      paddingHorizontal: utils.spacing.xl,
                      paddingVertical: utils.spacing.md,
                      borderRadius: utils.radius.md,
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      ...Platform.select({
                        web: {
                          boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          ':hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 28px ${addColorOpacity(tokens.color.primary, 0.45)}`,
                          },
                          ':active': {
                            transform: 'translateY(0px) scale(0.98)',
                          },
                        } as any,
                        default: {
                          shadowColor: tokens.color.primary,
                          shadowOffset: { width: 0, height: 6 },
                          shadowOpacity: 0.35,
                          shadowRadius: 20,
                          elevation: 8,
                          opacity: pressed ? 0.9 : 1,
                        },
                      }),
                    },
                  ]}
                >
                  <Text style={{
                    fontSize: buttonSize,
                    fontWeight: utils.typography.weights.semibold,
                    letterSpacing: 0.2,
                    color: tokens.color.onPrimary,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    Go to Login
                  </Text>
                </Pressable>
              </Animated.View>
            </Card>
          </Animated.View>
        </>
      );
    }

    if (requestStatus === 'pending') {
      return (
        <>
          <Animated.View 
            entering={FadeInUp.delay(100).springify()}
            style={{ marginBottom: utils.spacing['2xl'], alignItems: 'center' }}
          >
            <View style={{
              width: getResponsiveValue(80, 90, 100, 100),
              height: getResponsiveValue(80, 90, 100, 100),
              borderRadius: utils.radius.xl,
              backgroundColor: tokens.color.primary,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: utils.spacing.lg,
              ...Platform.select({
                web: {
                  boxShadow: `0 8px 32px ${addColorOpacity(tokens.color.primary, 0.25)}`,
                } as any,
                default: {
                  shadowColor: tokens.color.primary,
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.25,
                  shadowRadius: 32,
                  elevation: 8,
                },
              }),
            }}>
              <Ionicons 
                name="leaf" 
                size={getResponsiveValue(40, 44, 48, 48)} 
                color={tokens.color.onPrimary} 
              />
            </View>
            <Text style={[
              {
                fontSize: headingSize,
                fontWeight: utils.typography.weights.bold,
                textAlign: 'center',
                marginBottom: utils.spacing.md,
                letterSpacing: -0.5,
                color: tokens.color.onBackground,
                fontFamily: utils.typography.heading.fontFamily,
                lineHeight: headingSize * 1.2,
                width: '100%',
              }
            ]}>
              Request Pending
            </Text>
            <Text style={[
              {
                fontSize: subtitleSize,
                color: tokens.color.onBackground,
                textAlign: 'center',
                marginBottom: utils.spacing['2xl'],
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: subtitleSize * 1.5,
                width: '100%',
                paddingHorizontal: utils.spacing.md,
              }
            ]}>
              Your account request for {email} is pending approval. You will be able to create your account once an administrator reviews your request.
            </Text>
          </Animated.View>
          <Animated.View entering={FadeInUp.delay(200).springify()}>
            <Card style={{ 
              width: '100%',
              ...Platform.select({
                web: {
                  boxSizing: 'border-box',
                } as any,
              }),
            }}>
              <View style={styles.form}>
                <Input
                  placeholder="Email"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="email"
                  textContentType="emailAddress"
                  spellCheck={false}
                  editable={true}
                  style={{ opacity: 0.6 }}
                />
              </View>
              <Link href="/(auth)/login" asChild>
                <Pressable>
                  <Text style={[
                    {
                      fontSize: bodySize,
                      color: tokens.color.primary,
                      marginTop: utils.spacing.sm,
                      textAlign: 'center',
                      fontWeight: utils.typography.weights.medium,
                      fontFamily: utils.typography.body.fontFamily,
                    }
                  ]}>
                    Already have an account? Login
                  </Text>
                </Pressable>
              </Link>
            </Card>
          </Animated.View>
        </>
      );
    }

    if (requestStatus === 'denied') {
      return (
        <>
          <Animated.View 
            entering={FadeInUp.delay(100).springify()}
            style={{ marginBottom: utils.spacing['2xl'], alignItems: 'center' }}
          >
            <View style={{
              width: getResponsiveValue(80, 90, 100, 100),
              height: getResponsiveValue(80, 90, 100, 100),
              borderRadius: utils.radius.xl,
              backgroundColor: tokens.color.error,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: utils.spacing.lg,
              ...Platform.select({
                web: {
                  boxShadow: `0 8px 32px ${addColorOpacity(tokens.color.error, 0.25)}`,
                } as any,
                default: {
                  shadowColor: tokens.color.error,
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.25,
                  shadowRadius: 32,
                  elevation: 8,
                },
              }),
            }}>
              <Ionicons 
                name="close-circle" 
                size={getResponsiveValue(40, 44, 48, 48)} 
                color={tokens.color.onError} 
              />
            </View>
            <Text style={[
              {
                fontSize: headingSize,
                fontWeight: utils.typography.weights.bold,
                textAlign: 'center',
                marginBottom: utils.spacing.md,
                letterSpacing: -0.5,
                color: tokens.color.onBackground,
                fontFamily: utils.typography.heading.fontFamily,
                lineHeight: headingSize * 1.2,
                width: '100%',
              }
            ]}>
              Request Denied
            </Text>
            <Text style={[
              {
                fontSize: subtitleSize,
                color: tokens.color.onBackground,
                textAlign: 'center',
                marginBottom: utils.spacing['2xl'],
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: subtitleSize * 1.5,
                width: '100%',
                paddingHorizontal: utils.spacing.md,
              }
            ]}>
              Your account request for {email} was not approved. Please contact support if you believe this is an error.
            </Text>
          </Animated.View>
          <Animated.View entering={FadeInUp.delay(200).springify()}>
            <Card style={{ 
              width: '100%',
              ...Platform.select({
                web: {
                  boxSizing: 'border-box',
                } as any,
              }),
            }}>
              <View style={styles.form}>
                <Input
                  placeholder="Email"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="email"
                  textContentType="emailAddress"
                  spellCheck={false}
                  editable={true}
                />
              </View>
              <Link href="/(auth)/login" asChild>
                <Pressable>
                  <Text style={[
                    {
                      fontSize: bodySize,
                      color: tokens.color.primary,
                      marginTop: utils.spacing.sm,
                      textAlign: 'center',
                      fontWeight: utils.typography.weights.medium,
                      fontFamily: utils.typography.body.fontFamily,
                    }
                  ]}>
                    Already have an account? Login
                  </Text>
                </Pressable>
              </Link>
            </Card>
          </Animated.View>
        </>
      );
    }

    if (requestStatus === 'approved') {
      return (
        <>
          <Animated.View 
            entering={FadeInUp.delay(100).springify()}
            style={{ marginBottom: utils.spacing['2xl'], alignItems: 'center' }}
          >
            <View style={{
              width: getResponsiveValue(80, 90, 100, 100),
              height: getResponsiveValue(80, 90, 100, 100),
              borderRadius: utils.radius.xl,
              backgroundColor: tokens.color.success,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: utils.spacing.lg,
              ...Platform.select({
                web: {
                  boxShadow: `0 8px 32px ${addColorOpacity(tokens.color.success, 0.25)}`,
                } as any,
                default: {
                  shadowColor: tokens.color.success,
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.25,
                  shadowRadius: 32,
                  elevation: 8,
                },
              }),
            }}>
              <Ionicons 
                name="checkmark-circle" 
                size={getResponsiveValue(40, 44, 48, 48)} 
                color={tokens.color.onSuccess} 
              />
            </View>
            <Text style={[
              {
                fontSize: headingSize,
                fontWeight: utils.typography.weights.bold,
                textAlign: 'center',
                marginBottom: utils.spacing.md,
                letterSpacing: -0.5,
                color: tokens.color.onBackground,
                fontFamily: utils.typography.heading.fontFamily,
                lineHeight: headingSize * 1.2,
                width: '100%',
              }
            ]}>
              Complete Your Account
            </Text>
            <Text style={[
              {
                fontSize: subtitleSize,
                color: tokens.color.onBackground,
                textAlign: 'center',
                marginBottom: utils.spacing['2xl'],
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: subtitleSize * 1.5,
                width: '100%',
                paddingHorizontal: utils.spacing.md,
              }
            ]}>
              Your account request has been approved! Create a password to complete your account setup.
            </Text>
          </Animated.View>
          
          <Animated.View entering={FadeInUp.delay(200).springify()}>
            <Card style={{ 
              width: '100%',
              ...Platform.select({
                web: {
                  boxSizing: 'border-box',
                } as any,
              }),
            }}>
              <View style={styles.form}>
                <Input
                  label="Email"
                  placeholder="Email"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="email"
                  textContentType="emailAddress"
                  spellCheck={false}
                  editable={true}
                  style={{ opacity: 0.7 }}
                />
                <PasswordInput
                  label="Password"
                  placeholder="Password (min 8 characters, uppercase, lowercase, number)"
                  value={password}
                  onChangeText={setPassword}
                  returnKeyType="done"
                  onSubmitEditing={handleSignup}
                />
                <Animated.View style={[buttonAnimatedStyle, { width: '100%' }]}>
                  <Pressable
                    onPressIn={handleButtonPressIn}
                    onPressOut={handleButtonPressOut}
                    onPress={handleSignup}
                    disabled={loading}
                    style={({ pressed }) => [
                      {
                        backgroundColor: tokens.color.primary,
                        minHeight: getResponsiveValue(48, 50, 52, 52),
                        paddingHorizontal: utils.spacing.xl,
                        paddingVertical: utils.spacing.md,
                        borderRadius: utils.radius.md,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: utils.spacing.sm,
                        width: '100%',
                        opacity: loading ? 0.7 : 1,
                        ...Platform.select({
                          web: {
                            boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            ':hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 28px ${addColorOpacity(tokens.color.primary, 0.45)}`,
                            },
                            ':active': {
                              transform: 'translateY(0px) scale(0.98)',
                            },
                          } as any,
                          default: {
                            shadowColor: tokens.color.primary,
                            shadowOffset: { width: 0, height: 6 },
                            shadowOpacity: 0.35,
                            shadowRadius: 20,
                            elevation: 8,
                            opacity: pressed || loading ? 0.9 : 1,
                          },
                        }),
                      },
                    ]}
                  >
                    <Text style={{
                      fontSize: buttonSize,
                      fontWeight: utils.typography.weights.semibold,
                      letterSpacing: 0.2,
                      color: tokens.color.onPrimary,
                      fontFamily: utils.typography.body.fontFamily,
                    }}>
                      {loading ? 'Creating account...' : 'Complete Sign Up'}
                    </Text>
                  </Pressable>
                </Animated.View>
              </View>

              <Link href="/(auth)/login" asChild>
                <Pressable>
                  <Text style={[
                    {
                      fontSize: bodySize,
                      color: tokens.color.primary,
                      marginTop: utils.spacing.sm,
                      textAlign: 'center',
                      fontWeight: utils.typography.weights.medium,
                      fontFamily: utils.typography.body.fontFamily,
                    }
                  ]}>
                    Already have an account? Login
                  </Text>
                </Pressable>
              </Link>
            </Card>
          </Animated.View>
        </>
      );
    }

    // Default: no request - show request form
    return (
      <>
        <Animated.View 
          entering={FadeInUp.delay(100).springify()}
          style={{ marginBottom: utils.spacing['2xl'], alignItems: 'center' }}
        >
          <View style={{
            width: getResponsiveValue(80, 90, 100, 100),
            height: getResponsiveValue(80, 90, 100, 100),
            borderRadius: utils.radius.xl,
            backgroundColor: tokens.color.primary,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: utils.spacing.lg,
            ...Platform.select({
              web: {
                boxShadow: `0 8px 32px ${addColorOpacity(tokens.color.primary, 0.25)}`,
              } as any,
              default: {
                shadowColor: tokens.color.primary,
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.25,
                shadowRadius: 32,
                elevation: 8,
              },
            }),
          }}>
            <Ionicons 
              name="leaf" 
              size={getResponsiveValue(40, 44, 48, 48)} 
              color={tokens.color.onPrimary} 
            />
          </View>
          <Text style={[
            {
              fontSize: headingSize,
              fontWeight: utils.typography.weights.bold,
              textAlign: 'center',
              marginBottom: utils.spacing.md,
              letterSpacing: -0.5,
              color: tokens.color.onBackground,
              fontFamily: utils.typography.heading.fontFamily,
              lineHeight: headingSize * 1.2,
              width: '100%',
            }
          ]}>
            Request Account
          </Text>
          <Text style={[
            {
              fontSize: subtitleSize,
              color: tokens.color.onBackground,
              textAlign: 'center',
              marginBottom: utils.spacing['2xl'],
              fontFamily: utils.typography.body.fontFamily,
              lineHeight: subtitleSize * 1.5,
              width: '100%',
              paddingHorizontal: utils.spacing.md,
            }
          ]}>
            Request access to create an account. Your request will be reviewed by an administrator.
          </Text>
        </Animated.View>
        
        <Animated.View entering={FadeInUp.delay(200).springify()}>
          <Card style={{ 
            width: '100%',
            ...Platform.select({
              web: {
                boxSizing: 'border-box',
              } as any,
            }),
          }}>
            <View style={styles.form}>
              <Input
                label="Email address"
                placeholder="you@example.com"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="email"
                textContentType="emailAddress"
                spellCheck={false}
                autoFocus={!isMobile}
                returnKeyType="done"
                onSubmitEditing={handleRequestAccount}
              />
              {checkingStatus && (
                <Text style={[
                  {
                    fontSize: bodySize,
                    color: tokens.color.onBackground,
                    textAlign: 'center',
                    marginBottom: utils.spacing.sm,
                    fontFamily: utils.typography.body.fontFamily,
                  }
                ]}>
                  Checking request status...
                </Text>
              )}
              <Animated.View style={[buttonAnimatedStyle, { width: '100%' }]}>
                <Pressable
                  onPressIn={handleButtonPressIn}
                  onPressOut={handleButtonPressOut}
                  onPress={handleRequestAccount}
                  disabled={loading || checkingStatus}
                  style={({ pressed }) => [
                    {
                      backgroundColor: tokens.color.primary,
                      minHeight: getResponsiveValue(48, 50, 52, 52),
                      paddingHorizontal: utils.spacing.xl,
                      paddingVertical: utils.spacing.md,
                      borderRadius: utils.radius.md,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: utils.spacing.sm,
                      width: '100%',
                      opacity: loading || checkingStatus ? 0.7 : 1,
                      ...Platform.select({
                        web: {
                          boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`,
                          cursor: loading || checkingStatus ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          ':hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 28px ${addColorOpacity(tokens.color.primary, 0.45)}`,
                          },
                          ':active': {
                            transform: 'translateY(0px) scale(0.98)',
                          },
                        } as any,
                        default: {
                          shadowColor: tokens.color.primary,
                          shadowOffset: { width: 0, height: 6 },
                          shadowOpacity: 0.35,
                          shadowRadius: 20,
                          elevation: 8,
                          opacity: pressed || loading || checkingStatus ? 0.9 : 1,
                        },
                      }),
                    },
                  ]}
                >
                  <Text style={{
                    fontSize: buttonSize,
                    fontWeight: utils.typography.weights.semibold,
                    letterSpacing: 0.2,
                    color: tokens.color.onPrimary,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    {loading ? 'Submitting request...' : 'Request Account'}
                  </Text>
                </Pressable>
              </Animated.View>
            </View>

            <Link href="/(auth)/login" asChild>
              <Pressable>
                <Text style={[
                  {
                    fontSize: bodySize,
                    color: tokens.color.primary,
                    marginTop: utils.spacing.sm,
                    textAlign: 'center',
                    fontWeight: utils.typography.weights.medium,
                    fontFamily: utils.typography.body.fontFamily,
                  }
                ]}>
                  Already have an account? Login
                </Text>
              </Pressable>
            </Link>
          </Card>
        </Animated.View>
      </>
    );
  };

  return (
    <View style={[
      styles.container,
      {
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        ...Platform.select({
          web: {
            minHeight: '100vh',
            overflowX: 'hidden',
          } as any,
        }),
      },
    ]}>
      <ScrollView 
        contentContainerStyle={{
          flexGrow: 1,
          width: '100%',
          maxWidth: '100%',
          paddingHorizontal: containerPadding,
          paddingVertical: getResponsiveValue(
            utils.spacing.xl,
            utils.spacing['2xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl']
          ),
          ...Platform.select({
            web: {
              minHeight: '100vh',
              overflowX: 'hidden',
            } as any,
          }),
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={{
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <View style={{ 
          width: '100%', 
          maxWidth: containerMaxWidth,
          alignSelf: 'center',
          flex: 1,
          justifyContent: 'center',
          paddingVertical: getResponsiveValue(
            utils.spacing.lg,
            utils.spacing.xl,
            utils.spacing.xl,
            utils.spacing.xl
          ),
        }}>
          {renderContent()}
        </View>
      </ScrollView>
    </View>
  );
}
